import { Text, Tooltip } from '@/design-system';
import { xCount } from '@/utils/stringUtils';

import {
  DEFAULT_START_INDEX,
  PERCENTAGE_BAND_COLORS_MONOCHROME,
} from '../constants';
import { TreeNode } from '../types';

export const LeafRunsDisplay = ({ node }: { node: TreeNode }) => {
  const runCount = node.num_runs || 0;
  const totalJobRunPercentage = node.totalRunsInJob
    ? ((runCount / node.totalRunsInJob) * 100).toFixed(1)
    : '0';

  if (
    !node.parentClusterNumRuns ||
    runCount === 0 ||
    node.indexInParent === undefined
  ) {
    return (
      <Text variant="xs" color="tertiary">
        —
      </Text>
    );
  }

  const percentage = ((runCount / node.parentClusterNumRuns) * 100).toFixed(1);
  const inversePercentage = (100 - Number(percentage)).toFixed(1);

  const startIndex = Math.min(
    DEFAULT_START_INDEX,
    Math.max(0, PERCENTAGE_BAND_COLORS_MONOCHROME.length - (node.numPeers || 0))
  );

  // Use parentTotalRunPercentageOfCategory to determine bar width relative to peers at parent's depth
  const maxWidth = 200; // 200px for 100% of peers at parent's depth
  const entireWidth = node.parentTotalRunPercentageOfCategory
    ? (node.parentTotalRunPercentageOfCategory / 100) * maxWidth
    : maxWidth;

  return (
    <Tooltip title={xCount('run', runCount)} side="top">
      <div className="flex flex-row items-center gap-2 pl-2">
        <div
          className="flex min-h-[14px] flex-1 gap-[1px]"
          style={{ width: `${entireWidth}px` }}
        >
          <div
            style={{
              width: `${percentage}%`,
              backgroundColor:
                PERCENTAGE_BAND_COLORS_MONOCHROME[
                  (node.indexInParent + startIndex) %
                    PERCENTAGE_BAND_COLORS_MONOCHROME.length
                ],
              minWidth: '2px',
              minHeight: '8px',
              borderRadius: '2px',
            }}
          />
          <div
            className="bg-tertiary"
            style={{
              width: `${inversePercentage}%`,
              minWidth: '2px',
              minHeight: '8px',
              borderRadius: '2px',
            }}
          />
        </div>
        <Text variant="xs" weight="medium">
          {totalJobRunPercentage}%
        </Text>
      </div>
    </Tooltip>
  );
};

export const ParentRunsDisplay = ({ node }: { node: TreeNode }) => {
  const runCount = node.num_runs || 0;
  const totalJobRunPercentage = node.totalRunsInJob
    ? ((runCount / node.totalRunsInJob) * 100).toFixed(1)
    : '0';

  if (!runCount) {
    return (
      <Text variant="xs" color="tertiary">
        —
      </Text>
    );
  }

  const percentages = node.children?.length
    ? node.children
        ?.filter((child) => child.num_runs)
        ?.map((child) => {
          // We know we have child.num_runs here because we just filtered
          return ((child.num_runs! / runCount) * 100).toFixed(1);
        })
    : // else we are a leaf node and have no children, return an array of 1 100%
      [100];

  // Use totalRunPercentageOfCategory to determine bar width relative to peers at same depth
  const maxWidth = 200; // 200px for 100% of peers at this depth
  const entireWidth = node.totalRunPercentageOfCategory
    ? (node.totalRunPercentageOfCategory / 100) * maxWidth
    : maxWidth;

  const startIndex = Math.min(
    DEFAULT_START_INDEX,
    Math.max(
      0,
      PERCENTAGE_BAND_COLORS_MONOCHROME.length - (node.children?.length || 0)
    )
  );

  return (
    <Tooltip title={xCount('run', runCount)} side="top">
      <div
        className="flex flex-row items-center gap-2 overflow-x-auto rounded-sm border border-secondary p-2"
        style={{ width: `${entireWidth + 50}px` }}
      >
        <div
          className="flex min-h-[14px] flex-1 gap-[1px]"
          style={{ width: `${entireWidth}px` }}
        >
          {percentages?.map((percentage, index) => (
            <div
              key={index}
              style={{
                width: `${percentage}%`,
                minWidth: '2px',
                minHeight: '8px',
                borderRadius: '2px',
                backgroundColor:
                  PERCENTAGE_BAND_COLORS_MONOCHROME[
                    (index + startIndex) %
                      PERCENTAGE_BAND_COLORS_MONOCHROME.length
                  ],
              }}
            />
          ))}
        </div>
        <Text variant="xs" weight="medium">
          {totalJobRunPercentage}%
        </Text>
      </div>
    </Tooltip>
  );
};
